import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'

import { resolveExpandTransition, useThemeMotion } from '../animation/themeMotion'
import { comicPanelRoundSm } from './comic/comicPanelTokens'
import { IconChevronDown } from './nav-icons'

type Props = {
  angular: boolean
  title: string
  subtitle?: string
  open: boolean
  onToggle: () => void
  children: ReactNode
  className?: string
  /** `menu` 与工作流/侧栏表单卡片一致；默认 `muted` 保持旧行为 */
  surface?: 'muted' | 'menu'
}

const surfaceClass = {
  muted: 'bg-panel/30',
  menu: 'bg-[var(--menu-solid)]',
} as const

/** 可折叠区块（主题 expand 动效，与创作页提示词区一致） */
export function ChromeCollapsibleSection({
  angular,
  title,
  subtitle,
  open,
  onToggle,
  children,
  className = '',
  surface = 'muted',
}: Props) {
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(motionPreset, { reducedMotion: !!reducedMotion })

  return (
    <div
      className={[
        'overflow-hidden border border-line shadow-card',
        surfaceClass[surface],
        comicPanelRoundSm(angular),
        surface === 'menu' && angular ? 'border-2' : '',
        className,
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full min-w-0 items-center gap-2 px-3 py-2.5 text-left transition hover:bg-surface-muted/50"
      >
        <IconChevronDown
          className={[
            'h-4 w-4 shrink-0 text-ink-soft transition-transform duration-200',
            open ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">{title}</span>
          {subtitle ? (
            <span className="mt-0.5 block truncate text-xs text-ink-soft">{subtitle}</span>
          ) : null}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          reducedMotion ? (
            <div className="border-t border-line px-3 py-3">{children}</div>
          ) : (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={expandTrans}
              className="overflow-hidden border-t border-line"
            >
              <div className="px-3 py-3">{children}</div>
            </motion.div>
          )
        ) : null}
      </AnimatePresence>
    </div>
  )
}
